<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { uniq } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useFormatters } from '@/composables/useFormatters';
import type { EmitsToListeners } from '@/types/core';
import type { UiTableNewField } from '@/types/table';
import type {
  GetPaymentsHistoryPayoutSummaryResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type PayoutSummary = GetPaymentsHistoryPayoutSummaryResponse['payload'];
type CurrencyTotal = PayoutSummary['on_review']['totals_by_currency'][number];
type EstimateRow = Partial<Record<keyof PayoutSummary, CurrencyTotal>>;

interface Props {
  currencyCode?: string | null;
}

interface Emits {
  close: [];
}

export interface PaymentEstimatesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  currencyCode: null,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const api = useApi();
const { formatCurrencyValue } = useFormatters();
//#endregion

//#region state
const isVisible = ref(false);
const summary = ref<PayoutSummary | null>(null);
//#endregion

//#region computed
const tableRows = computed<EstimateRow[]>(() => {
  if (!summary.value) {
    return [];
  }

  const onReview = summary.value.on_review.totals_by_currency;
  const readyForPayout = summary.value.ready_for_payout.totals_by_currency;
  const totals = summary.value.totals.totals_by_currency;

  if (!onReview.length && !readyForPayout.length && !totals.length) {
    return [];
  }

  const currencyCodes = uniq(
    [...onReview, ...readyForPayout, ...totals].map((item) => item.currency_code),
  );

  const currencyRows = currencyCodes.map((currencyCode) => ({
    on_review: getAmount(onReview, currencyCode),
    ready_for_payout: getAmount(readyForPayout, currencyCode),
    totals: getAmount(totals, currencyCode),
  }));

  return [
    {
      on_review: summary.value.on_review.converted_total,
      ready_for_payout: summary.value.ready_for_payout.converted_total,
      totals: summary.value.totals.converted_total,
    },
    {},
    ...currencyRows,
  ];
});

const fields = computed<UiTableNewField<EstimateRow>[]>(() => [
  {
    name: 'on_review',
    computedValue: (row) => {
      if (isSectionRow(row)) {
        return t('payments.estimates.breakdown');
      }

      return formatTotal(row.on_review);
    },
  },
  {
    name: 'ready_for_payout',
    computedValue: (row) => formatTotal(row.ready_for_payout),
  },
  {
    name: 'totals',
    computedValue: (row) => formatTotal(row.totals),
  },
]);
//#endregion

//#region methods
function isSectionRow(row: EstimateRow) {
  return !row.on_review && !row.ready_for_payout && !row.totals;
}

function getAmount(items: CurrencyTotal[], currencyCode: string) {
  return items.find((item) => item.currency_code === currencyCode);
}

function formatTotal(item?: CurrencyTotal) {
  if (!item) {
    return '';
  }

  return formatCurrencyValue(item.currency_code, item.amount);
}

function spanMethod({
  row,
  columnIndex,
}: {
  row: EstimateRow;
  columnIndex: number;
}) {
  if (!isSectionRow(row)) {
    return;
  }

  if (columnIndex === 0) {
    return [1, 3];
  }

  return [0, 0];
}

function getRowClass(row: EstimateRow) {
  return isSectionRow(row) ? 'payment-estimates-popup__row_breakdown' : '';
}

async function fetchData() {
  const response = await api.getPaymentsHistoryPayoutSummary({
    currency_code: props.currencyCode,
  });
  summary.value = response.data.payload;
}

async function open() {
  await fetchData();
  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="payment-estimates-popup"
    :title="$t('payments.estimates.title')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <UiTableNew
        :fields="fields"
        :data="tableRows"
        :span-method="spanMethod"
        :computed-row-class="getRowClass"
        disable-sort
        :padding="0"
        is-static
        i18n-path="payments.estimates"
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex">
        <UiButton
          type="positive"
          size="big"
          icon="check"
          filled
          @click="handleClose"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.payment-estimates-popup {
  .ui-table__table {
    --table-row-highlight: transparent;
  }

  &__row_breakdown {
    td.ui-table__table-cell .cell {
      font-weight: 500;
      font-size: 14px;
    }
  }
}
</style>
